import React from "react";
import { RISKS, REG_ENTITIES, PRESS, LLOYDS_CERT_URL } from "../mock/data";
import { ShieldCheck, ExternalLink, FileText } from "lucide-react";
import { Wrap, SectionHead, Note } from "./Section";
import { useReveal } from "../hooks/useReveal";

function RiskCard({ r, i }) {
  const [ref, inView] = useReveal();
  return (
    <div ref={ref} className={`reveal ${inView ? "in" : ""} card rounded-2xl p-7`} style={{ animationDelay: `${i * 90}ms` }}>
      <div className="mono text-accent text-sm mb-4">{r.n}</div>
      <h3 className="display text-lg mb-3">{r.title}</h3>
      <p className="text-[13px] text-[var(--muted)] leading-relaxed">{r.body}</p>
    </div>
  );
}

export default function Risk() {
  return (
    <div id="risk">
      <Wrap>
        <SectionHead tag="Risk disclosure" title="Three risks," highlight="stated plainly"
          desc="Anyone who shows you returns without this section is selling you something. These are the three ways this can cost you money." />
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {RISKS.map((r, i) => <RiskCard key={i} r={r} i={i} />)}
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {[["Worst losing streak recorded", "2 consecutive trades"], ["Losses needed to close a 12× account", "200"], ["Losses needed to close a 24× account", "100"]].map((x, i) => (
            <div key={i} className="card rounded-2xl p-5">
              <div className="label text-[var(--muted)] text-[0.58rem] mb-2">{x[0]}</div>
              <div className="display text-xl text-accent">{x[1]}</div>
            </div>
          ))}
        </div>
        <Note>
          Historical figures describe what has happened, not what can happen. Markets can move further and faster than any past period — treat the capital you deposit as capital you can afford to lose in full.
        </Note>
      </Wrap>

      <Wrap>
        <SectionHead tag="Who holds the account" title="Regulation, oversight" highlight="and insurance"
          desc="A regulator requires a broker to keep client funds separate from company capital, to undergo periodic reporting and auditing, and to meet minimum capital requirements. The FSC oversees the Mauritius entity; the FSCA does the same for services provided to South African clients — the broker is accountable in two places, not one." />
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {REG_ENTITIES.map((e, i) => {
            const Icon = e.icon;
            return (
              <div key={i} className="card rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-6">
                  <Icon size={20} className="text-accent" strokeWidth={1.6} />
                  <span className="label text-[var(--muted)]">{e.region}</span>
                </div>
                <div className="space-y-3">
                  {e.rows.map((row, j) => (
                    <div key={j} className="flex items-center justify-between border-b border-white/[0.05] pb-3 last:border-0">
                      <span className="text-[13px] text-[var(--muted)]">{row[0]}</span>
                      <span className="text-[13px] text-[var(--text)] text-right ml-4">{row[1]}</span>
                    </div>
                  ))}
                </div>
                {e.link && (
                  <a href={e.link.href} target="_blank" rel="noreferrer"
                    className="mt-5 inline-flex items-center gap-2 btn-ghost px-4 py-2.5 rounded-lg">
                    {e.link.label} <ExternalLink size={13} />
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-5 card rounded-2xl p-7">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={20} className="text-accent" strokeWidth={1.6} />
            <span className="label text-[var(--muted)]">Lloyd's of London policy</span>
          </div>
          <div className="display text-3xl text-accent mb-2">Up to USD 1,000,000</div>
          <p className="text-[13px] text-[var(--muted)] mb-4">in respect of the net loss of any one claimant, subject to the terms, conditions and limits of the policy. Certificate period 29 April 2026 — 28 April 2027.</p>
          <ul className="space-y-2 text-[12px] text-[var(--muted)] mb-5">
            <li>• This insurance does NOT insure trading performance and does not protect against normal trading losses.</li>
            <li>• Coverage is subject to the terms, exclusions and limits of the actual policy.</li>
          </ul>
          <a href={LLOYDS_CERT_URL} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 btn-accent px-5 py-2.5 rounded-lg">
            <FileText size={14} /> View insurance certificate
          </a>
        </div>
        <Note>
          A CySEC application for the European entity is pending. Pending is not authorised — treat it as an intention, not a permission. Regulatory and insurance details are reproduced from the documents supplied by the broker and may change.
        </Note>
      </Wrap>

      <Wrap>
        <SectionHead tag="Press references" title="What the trade" highlight="press has written"
          desc="Coverage of TAG Markets and the amplified account structures. Press mentions describe a company's profile — they are not endorsements, audits or verification of any strategy's results." />
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {PRESS.map((p, i) => (
            <div key={i} className="card rounded-2xl p-7">
              <div className="flex items-center justify-between mb-4">
                <span className="label text-accent">{p.source}</span>
                <ExternalLink size={15} className="text-[var(--muted)]" />
              </div>
              <h3 className="text-[15px] font-semibold text-[var(--text)] leading-snug mb-3">{p.title}</h3>
              {p.quote && <p className="text-[13px] text-[var(--muted)] italic leading-relaxed">“{p.quote}”</p>}
            </div>
          ))}
        </div>
      </Wrap>
    </div>
  );
}
